import type { BufferGeometry, Mesh, Object3D } from "three";
import { BufferAttribute, Vector3 } from "three";
import { compactGeometry } from "./compactGeometry";

/**
 * Shared smooth shading for every part in the scene.
 *
 * The parts are all extrusions or castings, and their flat faces meet at real,
 * hard edges — but the curves do not. A die radius on a profile, the arc of a
 * panel cutout, the dome of a ball corner: those are polygons standing in for
 * a curve, and shading them face by face is the most visible "this is CAD"
 * tell in a picture, because the facets catch the environment one at a time as
 * the camera moves.
 *
 * One angle for the geometry the configurator builds and for the meshes it
 * imports, so the two kinds of part in a single picture agree with each other.
 *
 * The normals are computed here rather than by three's `toCreasedNormals` or
 * `computeVertexNormals`, because both weight the faces around a vertex in a
 * way that depends on how the surface happens to be triangulated. A CAD
 * exporter tessellates a flat flange as one or two enormous triangles and the
 * fillet next to it as hundreds of slivers, so at a vertex on the boundary the
 * two meet in wildly unequal numbers and sizes:
 *
 * - `computeVertexNormals` sums unnormalised cross products, which weights by
 *   area, so the flange dictates the normal of every vertex on the fillet's
 *   first row and the fillet shades as a flat step.
 * - `toCreasedNormals` sums unit normals, which weights by count, so a fan of
 *   slivers outvotes the flange and drags its normal off the plane. A sliver
 *   whose three points are all but collinear contributes a full vote in an
 *   essentially random direction on top of that.
 *
 * Weighting each face by the angle its corner makes at the vertex — what
 * Blender and most DCC tools do — is independent of both. A face that covers a
 * quarter of the surface around a vertex gets a quarter of the say, whether it
 * is one triangle or fifty, a square millimetre or a thousand, and a sliver's
 * acute corners weigh nothing.
 *
 * One thing angle weighting gets wrong, and it is the most visible thing of
 * all on chrome: where a flat face meets a fillet, the vertex on the boundary
 * averages the flat normal with the fillet's first row and comes out several
 * degrees off the plane. A flat face with a tilted vertex normal is no longer
 * flat — the shading interpolates the tilt across the whole triangle, each
 * triangle of the face gets a different gradient, and the triangulation shows
 * as streaks fanning across what should be a mirror. (Measured on the ball
 * corner: up to 8° off on the flange, 14° on the underside.) So the flat face
 * wins: when one *coplanar region* — a flat face, however it is triangulated —
 * is much larger than every other region meeting in a smoothing fan, the fan
 * takes that region's normal and stays exactly flat, and the fillet's first
 * row carries the transition instead, where a curve is expected to have one.
 * See {@link FLAT_DOMINANCE_RATIO}.
 */

/**
 * Edges meeting at a sharper angle than this stay hard, shallower ones are
 * smoothed into the curve they approximate. Measured between face normals, so
 * a 90° box edge is 90° here and a 45° chamfer is 45°.
 *
 * 30° clears every real edge of a case part — the square corners of an
 * extrusion, the chamfers on a panel or an angle section — by a wide margin,
 * while an arc tessellated into a dozen segments comes in under 10° per joint
 * and is smoothed.
 */
export const PART_CREASE_ANGLE_DEG = 30;

const PART_CREASE_COS = Math.cos((PART_CREASE_ANGLE_DEG * Math.PI) / 180);

/**
 * Default grid the vertex positions are snapped to before welding, in the
 * geometry's own units — a micron for the millimetre-space geometry built
 * here.
 *
 * Vertices are welded by exact position. Two vertices that should be
 * coincident but that miter or Bézier arithmetic leaves at 0.9999997 and
 * 1.0000001 are never averaged, which shows up as an occasional hard facet
 * along an otherwise smooth corner that moves or disappears as the part
 * changes size. Rounding first — orders of magnitude below any manufacturing
 * tolerance — makes them bit-identical, so they always weld.
 */
export const WELD_GRID_MM = 0.001;

/**
 * A triangle whose height is less than this fraction of its longest edge is
 * skipped: it is invisible, and its normal is noise.
 *
 * Such triangles come out of CAD tessellation wherever a T-junction is
 * stitched shut. Their cross product is the difference of two nearly equal
 * products, so at one part in a million the direction is decided by the last
 * couple of bits of the float32 positions, not by the surface. A legitimately
 * thin triangle — a hundredth of a millimetre of height across a 20 mm flange,
 * one part in two thousand — is three orders of magnitude clear of this and
 * keeps its (correct) normal.
 */
const DEGENERATE_HEIGHT_RATIO = 1e-6;

/**
 * Faces whose normals are within this angle across a shared edge belong to the
 * same coplanar region.
 *
 * A CAD exporter or `ExtrudeGeometry` triangulates a flat face into triangles
 * that are coplanar to float precision — a few millionths of a degree. The
 * facets of any curve this pipeline produces or imports are degrees apart:
 * even the 11 000-face ball corner's dome is over 2° per facet. A tenth of a
 * degree sits three orders of magnitude clear of both.
 */
const COPLANAR_ANGLE_DEG = 0.1;
const COPLANAR_COS = Math.cos((COPLANAR_ANGLE_DEG * Math.PI) / 180);

/**
 * A coplanar region locks the normal of a smoothing fan it belongs to when it
 * is at least this many times larger than every other region in the fan.
 *
 * The fillet rows, dome facets and cylinder strips that make up a curve are
 * of a size with their neighbours — under 2:1 even where a CAD tessellation
 * tightens towards a pole — so among themselves they keep the angle-weighted
 * average that shades a curve correctly. A flange against its fillet's first
 * row is hundreds to one; a profile face against a fillet chord, nearly a
 * hundred. Four separates the two cases with room on both sides.
 */
export const FLAT_DOMINANCE_RATIO = 4;

export type CreasedNormalsOptions = {
  /**
   * Grid to snap positions to before welding, in the geometry's own units.
   *
   * Pass 0 to skip, which is what imported meshes want: their units are
   * whatever the file was authored in (`meshInfo.transform.scale` is applied
   * to the object, not baked into the geometry), so a millimetre-sized grid
   * could be a metre-sized one and would visibly deform the part. They also
   * do not need it — a loader emits coincident vertices from one parsed float,
   * so they are already bit-identical.
   */
  weldGrid?: number;
};

function quantizePositions(geometry: BufferGeometry, grid: number): void {
  const positions = geometry.getAttribute("position");
  if (!positions) return;

  const snapped = new Vector3();
  for (let i = 0; i < positions.count; i++) {
    snapped.fromBufferAttribute(positions, i).divideScalar(grid);
    positions.setXYZ(
      i,
      Math.round(snapped.x) * grid,
      Math.round(snapped.y) * grid,
      Math.round(snapped.z) * grid,
    );
  }
  positions.needsUpdate = true;
}

/**
 * Welded vertex id for every corner of a non-indexed geometry: corners at the
 * same position get the same id, so faces can be matched up by shared edges.
 */
function weldCorners(geometry: BufferGeometry): Uint32Array {
  const positions = geometry.getAttribute("position");
  const ids = new Uint32Array(positions.count);
  // Nested maps rather than one keyed on `${x},${y},${z}`: this runs once per
  // corner, and an imported mesh has hundreds of thousands of them, so the
  // string form spends most of its time formatting three doubles and then
  // collecting the garbage. `Map` compares keys by SameValueZero, which for
  // these coordinates agrees with the string exactly — it folds -0 into 0 and
  // matches NaN with NaN just as "0" and "NaN" did.
  const byX = new Map<number, Map<number, Map<number, number>>>();
  let nextId = 0;

  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i);
    let byY = byX.get(x);
    if (byY === undefined) {
      byY = new Map();
      byX.set(x, byY);
    }
    const y = positions.getY(i);
    let byZ = byY.get(y);
    if (byZ === undefined) {
      byZ = new Map();
      byY.set(y, byZ);
    }
    const z = positions.getZ(i);
    let id = byZ.get(z);
    if (id === undefined) {
      id = nextId++;
      byZ.set(z, id);
    }
    ids[i] = id;
  }

  return ids;
}

/**
 * Union-find over the corners of the mesh. Two corners end up in the same set
 * when they sit at the same vertex and their faces are joined, directly or
 * through other faces, by edges shallower than the crease angle — a smoothing
 * fan, in Blender's terms. Every corner in a set gets the same normal.
 */
function findRoot(parent: Uint32Array, corner: number): number {
  let root = corner;
  while (parent[root] !== root) {
    root = parent[root] as number;
  }
  // Path compression: point everything on the way at the root.
  let current = corner;
  while (parent[current] !== root) {
    const next = parent[current] as number;
    parent[current] = root;
    current = next;
  }
  return root;
}

function union(parent: Uint32Array, a: number, b: number): void {
  const rootA = findRoot(parent, a);
  const rootB = findRoot(parent, b);
  if (rootA !== rootB) {
    parent[rootB] = rootA;
  }
}

/**
 * The corner of face `face` that sits at welded vertex `vertex`, or -1.
 */
function cornerAt(
  vertexIds: Uint32Array,
  face: number,
  vertex: number,
): number {
  const first = face * 3;
  for (let c = first; c < first + 3; c++) {
    if (vertexIds[c] === vertex) return c;
  }
  return -1;
}

function edgeKey(a: number, b: number): number {
  // Vertex ids are dense from 0, so a pair packs into one double exactly for
  // any mesh under 2^26 vertices.
  return a < b ? a * 0x4000000 + b : b * 0x4000000 + a;
}

function measureFaces(
  positions: ReturnType<BufferGeometry["getAttribute"]>,
  faceNormals: Float64Array,
  faceAreas: Float64Array,
  cornerAngles: Float64Array,
  faceIsValid: Uint8Array,
  normal: Vector3,
): void {
  const faceCount = faceIsValid.length;
  const cornerA = new Vector3();
  const cornerB = new Vector3();
  const cornerC = new Vector3();
  const ab = new Vector3();
  const bc = new Vector3();
  const ca = new Vector3();
  for (let face = 0; face < faceCount; face++) {
    const i = face * 3;
    cornerA.fromBufferAttribute(positions, i);
    cornerB.fromBufferAttribute(positions, i + 1);
    cornerC.fromBufferAttribute(positions, i + 2);
    ab.subVectors(cornerB, cornerA);
    bc.subVectors(cornerC, cornerB);
    ca.subVectors(cornerA, cornerC);

    // |ab × ac| is twice the area, i.e. longest edge times height.
    normal.crossVectors(ab, ca).negate();
    const twiceArea = normal.length();
    const longestEdge = Math.max(ab.length(), bc.length(), ca.length());
    // Keep the positive comparison: a NaN area or threshold must fail it.
    const hasValidArea =
      twiceArea > DEGENERATE_HEIGHT_RATIO * longestEdge * longestEdge &&
      Number.isFinite(twiceArea);
    if (!hasValidArea) continue;

    normal.divideScalar(twiceArea);
    faceIsValid[face] = 1;
    faceAreas[face] = twiceArea / 2;
    faceNormals[i] = normal.x;
    faceNormals[i + 1] = normal.y;
    faceNormals[i + 2] = normal.z;

    // Interior angles: at a between ab and -ca, at b between bc and -ab, at c
    // between ca and -bc. `angleTo` clamps its cosine, so slivers are safe.
    cornerAngles[i] = ab.angleTo(ca.negate());
    ca.negate();
    cornerAngles[i + 1] = bc.angleTo(ab.negate());
    ab.negate();
    cornerAngles[i + 2] = ca.angleTo(bc.negate());
  }
}

function collectFacesByEdge(
  vertexIds: Uint32Array,
  faceIsValid: Uint8Array,
): Map<number, number[]> {
  // Faces per edge, so neighbours across a smooth edge can be joined.
  const faceCount = faceIsValid.length;
  const facesByEdge = new Map<number, number[]>();
  for (let face = 0; face < faceCount; face++) {
    if (!faceIsValid[face]) continue;
    const i = face * 3;
    for (let e = 0; e < 3; e++) {
      const key = edgeKey(
        vertexIds[i + e] as number,
        vertexIds[i + ((e + 1) % 3)] as number,
      );
      const faces = facesByEdge.get(key);
      if (faces) {
        faces.push(face);
      } else {
        facesByEdge.set(key, [face]);
      }
    }
  }
  return facesByEdge;
}

function joinSharedCorners(
  vertexIds: Uint32Array,
  parent: Uint32Array,
  f: number,
  g: number,
): void {
  // Smooth edge: join the two corners at each of its ends.
  const fFirst = f * 3;
  for (let e = 0; e < 3; e++) {
    const vertex = vertexIds[fFirst + e] as number;
    const gCorner = cornerAt(vertexIds, g, vertex);
    if (gCorner >= 0) {
      union(parent, fFirst + e, gCorner);
    }
  }
}

function joinSmoothingFans(
  facesByEdge: Map<number, number[]>,
  faceNormals: Float64Array,
  vertexIds: Uint32Array,
  parent: Uint32Array,
  regionParent: Uint32Array,
  creaseCos: number,
): void {
  const normalF = new Vector3();
  const normalG = new Vector3();
  for (const faces of facesByEdge.values()) {
    if (faces.length < 2) continue;
    for (let x = 0; x < faces.length; x++) {
      const f = faces[x] as number;
      normalF.fromArray(faceNormals, f * 3);
      for (let y = x + 1; y < faces.length; y++) {
        const g = faces[y] as number;
        normalG.fromArray(faceNormals, g * 3);
        const cos = normalF.dot(normalG);
        if (cos < creaseCos) continue;
        if (cos >= COPLANAR_COS) union(regionParent, f, g);

        joinSharedCorners(vertexIds, parent, f, g);
      }
    }
  }
}

function sumRegionAreas(
  faceIsValid: Uint8Array,
  faceAreas: Float64Array,
  regionParent: Uint32Array,
): Float64Array {
  // Area of each coplanar region, on its root.
  const faceCount = faceIsValid.length;
  const regionAreas = new Float64Array(faceCount);
  for (let face = 0; face < faceCount; face++) {
    if (!faceIsValid[face]) continue;
    const root = findRoot(regionParent, face);
    regionAreas[root] =
      (regionAreas[root] as number) + (faceAreas[face] as number);
  }
  return regionAreas;
}

function findFanFlatRegions(
  faceIsValid: Uint8Array,
  regionParent: Uint32Array,
  regionAreas: Float64Array,
  parent: Uint32Array,
  fanFlatRegion: Int32Array,
  fanFlatArea: Float64Array,
  fanRunnerUpArea: Float64Array,
): void {
  // Per fan: the largest region meeting in it, and the largest area among the
  // others. A fan seen through several corners of one region records that
  // region once — the comparison is between distinct regions, not corners.
  const faceCount = faceIsValid.length;
  for (let face = 0; face < faceCount; face++) {
    if (!faceIsValid[face]) continue;
    const region = findRoot(regionParent, face);
    const area = regionAreas[region] as number;
    for (let corner = face * 3; corner < face * 3 + 3; corner++) {
      const fan = findRoot(parent, corner);
      if (fanFlatRegion[fan] === region) continue;
      if (area > (fanFlatArea[fan] as number)) {
        fanRunnerUpArea[fan] = fanFlatArea[fan] as number;
        fanFlatArea[fan] = area;
        fanFlatRegion[fan] = region;
      } else if (area > (fanRunnerUpArea[fan] as number)) {
        fanRunnerUpArea[fan] = area;
      }
    }
  }
}

/**
 * Angle-weighted normals with hard creases, written into the `normal`
 * attribute of a non-indexed geometry.
 */
function computeCreasedNormals(geometry: BufferGeometry, creaseCos: number) {
  // `applyCreasedNormalsDeep` walks whatever a loader produced, and three's
  // types declare `getAttribute` as returning an attribute rather than an
  // optional one — so a geometry without positions reaches here typed as if it
  // had them. Skipping it is what `quantizePositions` already does; throwing
  // would abandon every mesh after it in the traversal.
  const positions = geometry.getAttribute("position");
  if (!positions) return;
  const cornerCount = positions.count;
  const faceCount = Math.floor(cornerCount / 3);
  const vertexIds = weldCorners(geometry);

  // Per face: unit normal and area, left zero for degenerate faces. Per corner:
  // interior angle, the weight it carries.
  const faceNormals = new Float64Array(faceCount * 3);
  const faceAreas = new Float64Array(faceCount);
  const cornerAngles = new Float64Array(cornerCount);
  const faceIsValid = new Uint8Array(faceCount);
  const normal = new Vector3();
  measureFaces(
    positions,
    faceNormals,
    faceAreas,
    cornerAngles,
    faceIsValid,
    normal,
  );

  const facesByEdge = collectFacesByEdge(vertexIds, faceIsValid);

  // Two union-finds over the same edges: corners into smoothing fans, and
  // faces into coplanar regions.
  const parent = new Uint32Array(cornerCount);
  for (let corner = 0; corner < cornerCount; corner++) {
    parent[corner] = corner;
  }
  const regionParent = new Uint32Array(faceCount);
  for (let face = 0; face < faceCount; face++) {
    regionParent[face] = face;
  }
  joinSmoothingFans(
    facesByEdge,
    faceNormals,
    vertexIds,
    parent,
    regionParent,
    creaseCos,
  );

  const regionAreas = sumRegionAreas(faceIsValid, faceAreas, regionParent);
  const fanFlatRegion = new Int32Array(cornerCount).fill(-1);
  const fanFlatArea = new Float64Array(cornerCount);
  const fanRunnerUpArea = new Float64Array(cornerCount);
  findFanFlatRegions(
    faceIsValid,
    regionParent,
    regionAreas,
    parent,
    fanFlatRegion,
    fanFlatArea,
    fanRunnerUpArea,
  );

  // Accumulate angle-weighted face normals per fan, then hand every corner
  // the normalised sum of its fan — or the flat region's normal, where one
  // dominates.
  const sums = new Float64Array(cornerCount * 3);
  for (let face = 0; face < faceCount; face++) {
    if (!faceIsValid[face]) continue;
    const i = face * 3;
    for (let corner = i; corner < i + 3; corner++) {
      const root = findRoot(parent, corner) * 3;
      const weight = cornerAngles[corner] as number;
      normal.fromArray(faceNormals, i).multiplyScalar(weight);
      sums[root] = (sums[root] as number) + normal.x;
      sums[root + 1] = (sums[root + 1] as number) + normal.y;
      sums[root + 2] = (sums[root + 2] as number) + normal.z;
    }
  }

  const normals = new Float32Array(cornerCount * 3);
  for (let corner = 0; corner < cornerCount; corner++) {
    const fan = findRoot(parent, corner);
    const root = fan * 3;
    const flatRegion = fanFlatRegion[fan] as number;
    const flatDominates =
      flatRegion >= 0 &&
      (fanRunnerUpArea[fan] as number) > 0 &&
      (fanFlatArea[fan] as number) >=
        FLAT_DOMINANCE_RATIO * (fanRunnerUpArea[fan] as number);
    if (flatDominates) {
      normal.fromArray(faceNormals, flatRegion * 3);
    } else {
      normal.fromArray(sums, root);
    }
    const length = normal.length();
    if (length > 0) {
      normal.divideScalar(length);
    } else if (faceIsValid[Math.floor(corner / 3)]) {
      // Contributions cancelled out exactly; fall back to the face.
      normal.fromArray(faceNormals, Math.floor(corner / 3) * 3);
    } else {
      // Nothing to average and no face to fall back on: up, rather than a zero
      // that `normalize()` in three's shading would turn into a NaN and spread
      // through the lighting and the AO G-buffer.
      normal.set(0, 1, 0);
    }
    normals[corner * 3] = normal.x;
    normals[corner * 3 + 1] = normal.y;
    normals[corner * 3 + 2] = normal.z;
  }

  geometry.setAttribute("normal", new BufferAttribute(normals, 3));
}

/**
 * Replaces a geometry's normals with creased ones at {@link
 * PART_CREASE_ANGLE_DEG}.
 *
 * Each vertex normal is the angle-weighted average of the faces in its
 * smoothing fan: the faces around the vertex that are connected to each other
 * by edges shallower than the crease angle. Faces across a sharper edge form a
 * fan of their own, so the vertex gets one normal per fan and the edge stays
 * hard. Degenerate triangles are ignored.
 *
 * Returns the same instance for non-indexed geometry (`ExtrudeGeometry` and
 * anything `OBJLoader` or `STLLoader` produces), so UVs and vertex order
 * survive and the caller's reference stays valid. Indexed input is converted,
 * and the result is a new geometry — assign what comes back rather than
 * relying on the argument.
 */
export function applyCreasedNormals(
  geometry: BufferGeometry,
  { weldGrid = WELD_GRID_MM }: CreasedNormalsOptions = {},
): BufferGeometry {
  const result = geometry.index ? geometry.toNonIndexed() : geometry;
  if (weldGrid > 0) {
    quantizePositions(result, weldGrid);
  }
  computeCreasedNormals(result, PART_CREASE_COS);
  return result;
}

/**
 * Creases every mesh under `object`, in place.
 *
 * Geometry shared between several meshes is creased once: an indexed geometry
 * comes back as a new instance, so without the cache the second mesh would keep
 * pointing at the original and shade differently from the first.
 */
export function applyCreasedNormalsDeep(
  object: Object3D,
  options: CreasedNormalsOptions & { compact?: boolean } = {},
): void {
  const creased = new Map<BufferGeometry, BufferGeometry>();

  object.traverse((child) => {
    const mesh = child as Mesh;
    // Lines have no normals to crease, and `isMesh` covers InstancedMesh too.
    if (!mesh.isMesh || !mesh.geometry) return;

    const already = creased.get(mesh.geometry);
    if (already) {
      mesh.geometry = already;
      return;
    }

    const source = mesh.geometry;
    const result = applyCreasedNormals(source, options);
    if (options.compact) compactGeometry(result);
    creased.set(source, result);
    mesh.geometry = result;

    if (result !== source) {
      source.dispose();
    }
  });
}
